import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { AuthService, type User } from '../services/auth';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const u = await AuthService.getCurrentUser();
        setUser(u);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
        console.error('Auth initialization failed:', err);
        setError(errorMessage);
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    error,
    async login(email: string, password: string) {
      try {
        const u = await AuthService.login(email, password);
        setUser(u);
        return u;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Login failed';
        console.error('Login failed:', err);
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    async logout() {
      try {
        await AuthService.logout();
        setUser(null);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Logout failed';
        console.error('Logout failed:', err);
        setError(errorMessage);
        setUser(null);
      }
    },
    async refresh() {
      try {
        const u = await AuthService.getCurrentUser();
        setUser(u);
      } catch {
        setUser(null);
      }
    },
  }), [user, loading, error]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
